import { hashKey, useQuery } from '@tanstack/react-query';
import { head, isEmpty, uniqBy } from 'lodash-es';
import { useCallback, useMemo, useRef } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useAppPerformanceContext } from 'src/contexts/AppPerformanceContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useDebouncedValueSafe } from 'src/hooks/useDebouncedValueSafe';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { getSectionInfoDisplay } from 'src/utils/autoPricingUtils';
import { PricingCalculationCacheTimeInMs } from 'src/utils/constants/constants';
import { filterSectionByZone } from 'src/utils/venueConfigUtils';
import {
  Feature,
  ListingClient,
  ListingDetailsPricingUpdates,
  ListingPriceCalculation,
  SectionInfo,
  VenueMapInfo,
} from 'src/WebApiController';

export type ListingPricingInputWithId = ListingDetailsPricingUpdates & {
  id: number;
};

export const useAutoPricingPreview = (
  viagogoEventId: number | null,
  listingId?: number | undefined,
  listingInput?: ListingPricingInputWithId
) => {
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);
  const hasVeloCBFeature = useUserHasFeature(
    Feature.AutoPricingImprovedPricePreivewForVeloCB
  );
  const hasBulkPriceCalcCancellationFix = useUserHasFeature(
    Feature.BulkPriceCalcRequestCancellation
  );

  const [debouncedInput] = useDebouncedValueSafe(listingInput, 300);

  const { trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackEndpoint } = useAppPerformanceContext();

  const shouldQuery = useMemo(() => {
    if (
      activeAccountWebClientConfig.activeAccountId == null ||
      !hasAutoPricingFeature
    ) {
      return false;
    }
    if (viagogoEventId == null || listingId == null || debouncedInput == null) {
      return false;
    }
    return listingId === debouncedInput.id;
  }, [
    activeAccountWebClientConfig.activeAccountId,
    hasAutoPricingFeature,
    listingId,
    debouncedInput,
    viagogoEventId,
  ]);

  const sanitizedInput = useMemo((): ListingPricingInputWithId | undefined => {
    if (debouncedInput == null) {
      return undefined;
    }
    return hasVeloCBFeature
      ? debouncedInput
      : {
          ...debouncedInput,
          velocityCircuitBreakerStateRowVersion: null,
        };
  }, [debouncedInput, hasVeloCBFeature]);

  const activeAbortControllerRef = useRef<AbortController | null>(null);

  const priceCalcQuery = useQuery({
    queryKey: [
      'ListingClient.getListingPriceCalculation',
      viagogoEventId,
      hashKey([listingId, debouncedInput]),
    ],
    queryFn: async ({ signal }) => {
      if (sanitizedInput == null || isEmpty(sanitizedInput)) {
        return null;
      }
      const { id: inputListingId, ...input } = sanitizedInput;

      // Cancel any in-flight request before starting — covers both retries
      // (React Query does not abort between attempts) and new query key
      // invocations.
      activeAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      activeAbortControllerRef.current = abortController;
      // Forward React Query's own cancellation (unmount / manual cancel) so
      // the request is also aborted when the query observer goes away.
      if (hasBulkPriceCalcCancellationFix) {
        signal.addEventListener('abort', () => abortController.abort());
      }

      return await trackEndpoint(
        'ListingClient.getListingPriceCalculation',
        new ListingClient(
          activeAccountWebClientConfig
        ).getListingPriceCalculation(
          viagogoEventId ?? undefined,
          listingId ?? undefined,
          input,
          abortController.signal
        ),
        (e: Error) => {
          abortController.abort(e.message);
        },
        5000 // 5s timeout to prevent hanging requests in case of network issues or server slowness
      );
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
    // Cache results for 30s to avoid redundant API calls for identical inputs.
    // GC time matches stale time to bound memory usage, since each unique input
    // produces a large response that we don't want to retain longer than needed.
    staleTime: PricingCalculationCacheTimeInMs,
    gcTime: PricingCalculationCacheTimeInMs,
    meta: {
      onError: (error: ErrorTypes) => {
        if (
          error instanceof Error &&
          error.message === 'signal is aborted without reason'
        ) {
          return;
        }
        trackError('ListingClient.getListingPriceCalculation', error, {
          trackErrorData: {
            activeAccountId: activeAccountWebClientConfig.activeAccountId,
            viagogoEventId,
            listingId,
            debouncedInput,
          },
        });
      },
    },
  });

  return useMemo(() => ({ priceCalcQuery }), [priceCalcQuery]);
};

export const useVenueMapHandlers = (
  filterSections: SectionInfo[],
  pricingPreview?: ListingPriceCalculation,
  venueMapInfo?: VenueMapInfo | null,
  onSelectedSectionsChange?: (selectedSections: SectionInfo[]) => void,
  onToggleRecommendingSectionIds?: (
    sectionIds: number[],
    option?: { include?: boolean; exclude?: boolean }
  ) => void
) => {
  const filterSectionsRef = useRef(filterSections);
  filterSectionsRef.current = filterSections;
  const onShiftSelect = useCallback(
    (sectionInfo: SectionInfo) => {
      // We are using the ticketClassId to group the sections
      const selectedClassId = head(
        sectionInfo?.rows
          ?.map(({ tktClass: ticketClass }) => ticketClass?.id)
          .filter((id) => !!id)
      );
      const classSections =
        venueMapInfo?.sections?.filter(filterSectionByZone(selectedClassId)) ||
        [];

      const classedSectionIds = classSections.map(({ id }) => id);
      const filteredSeciontIds = filterSectionsRef.current.map(({ id }) => id);
      let cloned = [...filterSectionsRef.current];
      if (classedSectionIds.every((id) => filteredSeciontIds.includes(id))) {
        cloned = cloned.filter(({ id }) => !classedSectionIds.includes(id));
        onToggleRecommendingSectionIds?.(classedSectionIds, { include: true });
      } else {
        for (const section of classSections) {
          if (!filteredSeciontIds.includes(section.id)) {
            cloned.push(section);
          }
        }
        onToggleRecommendingSectionIds?.(classedSectionIds, { exclude: true });
      }
      onSelectedSectionsChange?.(cloned);
    },
    [
      onSelectedSectionsChange,
      onToggleRecommendingSectionIds,
      venueMapInfo?.sections,
    ]
  );

  const onSingleSelect = useCallback(
    (section: SectionInfo) => {
      const idx = filterSectionsRef.current.findIndex(
        ({ id }) => id === section.id
      );
      const cloned = [...filterSectionsRef.current];
      if (idx === -1) {
        cloned.push(section);
      } else {
        cloned.splice(idx, 1);
      }
      onToggleRecommendingSectionIds?.([section.id]);
      onSelectedSectionsChange?.(cloned);
    },
    [onSelectedSectionsChange, onToggleRecommendingSectionIds]
  );

  const onSectionClicked = useCallback(
    (e: MouseEvent, sectionInfo: SectionInfo) => {
      if (e.shiftKey) {
        onShiftSelect(sectionInfo);
        return;
      }

      onSingleSelect(sectionInfo);
    },
    [onSingleSelect, onShiftSelect]
  );

  const onToggleMirrors = useCallback(
    (mirrors: SectionInfo[], exclude?: boolean) => {
      if (exclude) {
        const idsToExclude = mirrors.map(({ id }) => id);
        const updated = uniqBy(
          filterSectionsRef.current.filter(
            ({ id }) => !idsToExclude.includes(id)
          ),
          'id'
        ).toSorted((a, b) => a.name.localeCompare(b.name));
        onSelectedSectionsChange?.(updated);
        onToggleRecommendingSectionIds?.(idsToExclude, { include: true });
      } else {
        const updated = uniqBy(
          [...filterSectionsRef.current, ...mirrors],
          'id'
        ).toSorted((a, b) => a.name.localeCompare(b.name));
        onSelectedSectionsChange?.(updated);
        onToggleRecommendingSectionIds?.(
          mirrors.map(({ id }) => id),
          { exclude: true }
        );
      }
    },
    [onSelectedSectionsChange, onToggleRecommendingSectionIds]
  );

  const onSectionHovered = useCallback(
    (hoveredSection: SectionInfo) => {
      return getSectionInfoDisplay(
        hoveredSection,
        pricingPreview?.compListings ?? [],
        venueMapInfo?.sectionScores
      );
    },
    [pricingPreview?.compListings, venueMapInfo?.sectionScores]
  );

  const onSectionTapAndHold = useCallback(
    (e: MouseEvent | TouchEvent, sectionInfo: SectionInfo) => {
      e.stopPropagation();
      e.preventDefault();
      // Same logic as shift+click - select all sections in the same zone
      const selectedClassId = head(
        sectionInfo?.rows
          ?.map(({ tktClass: ticketClass }) => ticketClass?.id)
          .filter((id) => !!id)
      );
      const classSections =
        venueMapInfo?.sections?.filter(filterSectionByZone(selectedClassId)) ||
        [];

      const classedSectionIds = classSections.map(({ id }) => id);
      const filteredSectionIds = filterSectionsRef.current.map(({ id }) => id);
      let cloned = [...filterSectionsRef.current];

      if (classedSectionIds.every((id) => filteredSectionIds.includes(id))) {
        // If all sections in zone are selected, deselect them
        cloned = cloned.filter(({ id }) => !classedSectionIds.includes(id));
        onToggleRecommendingSectionIds?.(classedSectionIds, { include: true });
      } else {
        // Select all sections in the zone
        for (const section of classSections) {
          if (!filteredSectionIds.includes(section.id)) {
            cloned.push(section);
          }
        }
        onToggleRecommendingSectionIds?.(classedSectionIds, { exclude: true });
      }
      onSelectedSectionsChange?.(cloned);
    },
    [
      onSelectedSectionsChange,
      onToggleRecommendingSectionIds,
      venueMapInfo?.sections,
    ]
  );

  return useMemo(
    () => ({
      onSectionClicked,
      onToggleMirrors,
      onSectionHovered,
      onSectionTapAndHold,
    }),
    [onSectionClicked, onSectionHovered, onSectionTapAndHold, onToggleMirrors]
  );
};
